import { createAsyncThunk } from "@reduxjs/toolkit";
import PartiesInvolvedActionsAPI, {
  AddPartiesInvolvedRequest,
  UpdatePartyInvolvedRequest,
  UpsertPartiesInvolvedRequest,
} from "../../../api/PartiesInvolved";
import {
  PartyOptionsAPI,
  PartyOptionsResponse,
} from "../../../api/PartyOptions";
import { MessageResponse } from "../../../api/common";
import { RootState } from "../../../app/store";

export const addPartiesInvolvedThunk = createAsyncThunk<
  MessageResponse,
  { case_id: string; request: AddPartiesInvolvedRequest },
  { rejectValue: string }
>(
  "partiesInvolvedActions/add",
  async ({ case_id, request }, { rejectWithValue }) => {
    try {
      return await PartiesInvolvedActionsAPI.add(case_id, request);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to add parties involved data"
      );
    }
  }
);

export const updatePartyInvolvedThunk = createAsyncThunk<
  MessageResponse,
  { case_id: string; party_id: string; request: UpdatePartyInvolvedRequest },
  { rejectValue: string }
>(
  "partiesInvolvedActions/update",
  async ({ case_id, party_id, request }, { rejectWithValue }) => {
    try {
      return await PartiesInvolvedActionsAPI.update(case_id, party_id, request);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update party involved data"
      );
    }
  }
);

export const upsertPartiesInvolvedThunk = createAsyncThunk<
  MessageResponse,
  { case_id: string; request: UpsertPartiesInvolvedRequest },
  { rejectValue: string }
>(
  "partiesInvolvedActions/upsert",
  async ({ case_id, request }, { rejectWithValue }) => {
    try {
      return await PartiesInvolvedActionsAPI.upsert(case_id, request);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to upsert parties involved"
      );
    }
  }
);

export const deletePartyInvolvedThunk = createAsyncThunk<
  MessageResponse,
  { case_id: string; party_id: string },
  { rejectValue: string }
>(
  "partiesInvolvedActions/delete",
  async ({ case_id, party_id }, { rejectWithValue }) => {
    try {
      return await PartiesInvolvedActionsAPI.delete(case_id, party_id);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to delete party involved"
      );
    }
  }
);

export const fetchPartyOptionsThunk = createAsyncThunk<
  PartyOptionsResponse,
  string,
  { rejectValue: string }
>(
  "partiesInvolvedActions/fetchPartyOptions",
  async (case_id, { rejectWithValue }) => {
    try {
      return await PartyOptionsAPI.get(case_id);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch party options");
    }
  },
  {
    condition: (case_id, { getState }) => {
      const state = getState() as RootState;
      const { partyOptionsStatus, partyOptionsCaseId } =
        state.partiesInvolvedData;

      if (
        partyOptionsStatus === "succeeded" &&
        partyOptionsCaseId === case_id
      ) {
        return false;
      }

      if (partyOptionsStatus === "loading") {
        return false;
      }

      return true;
    },
  }
);
